/**
 * 回放列表：本地录像（localStorage 最近 10 条）与在线录像（排行榜带 replayId 的成绩）。
 * ←→ 切换 本地/在线，↑↓ 选择，Z 播放，X 返回标题。
 * 版本不符（REPLAY_VERSION 升级或低于该模式最低版本）的录像标记不可播。
 */
import { VIEW_W, VIEW_H } from '../config.js';
import { Input } from '../engine/input.js';
import { menuNav, valueDir } from '../engine/menu.js';
import { CHARACTERS } from '../content/characters.js';
import { DIFFICULTIES, EXTRA_DIFF } from '../content/difficulties.js';
import { REPLAY_VERSION, isReplayCompatible } from '../replay.js';
import { t, tf, charName, diffName, getLang } from '../i18n.js';

const LOCAL_KEY = 'kimi-replays';

/** 角色显示名按语言：英文 romaji，中文 cnName。 */
function charLabel(id) {
  const c = CHARACTERS.find((x) => x.id === id);
  return c ? charName(c) : String(id || '?');
}

/** 难度标签按语言（未知 id 回落原样显示）。 */
const DIFFS = [...DIFFICULTIES, EXTRA_DIFF];
function diffLabel(id) {
  const d = DIFFS.find((x) => x.id === id);
  return d ? diffName(d) : String(id);
}

export const ReplayListScene = {
  mgr: null,

  enter() {
    this.tab = 'local'; // 'local' | 'online'
    this.idx = 0;
    this.local = this._loadLocal();
    this.online = null; // 在线成绩（带 replayId）
    this.error = null;
    this.fetchOnline();
  },

  exit() {},

  _loadLocal() {
    try {
      const list = JSON.parse(globalThis.localStorage?.getItem(LOCAL_KEY) || '[]');
      return Array.isArray(list) ? list : [];
    } catch (e) {
      return [];
    }
  },

  fetchOnline() {
    fetch('/api/scores?limit=20&hasReplay=1')
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        if (this.mgr.current !== this) return; // 跨 enter 的在飞请求丢弃（审计发现）
        // 版本不符的录像不列出（元数据有版本字段且与当前版本不一致）
        this.online = (data.scores || []).filter(
          (s) => s.replayVersion === undefined || isReplayCompatible({ version: s.replayVersion, mode: s.difficulty }),
        );
        this.error = null; // 成功后清错误（否则永久卡错误页——同类历史 bug）
      })
      .catch((e) => {
        this.error = String((e && e.message) || e);
        this.online = [];
      });
  },

  /** 当前列表项（本地为录像 payload，在线为成绩行）。 */
  _items() {
    return this.tab === 'local' ? this.local : this.online || [];
  },

  async _play() {
    const items = this._items();
    const item = items[this.idx];
    if (!item) return;
    let payload = item;
    if (this.tab === 'online') {
      // 在线：成绩行只有 replayId，需拉取录像本体
      try {
        const res = await fetch(`/api/replays/${item.replayId}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        payload = await res.json();
      } catch (e) {
        this.error = String((e && e.message) || e);
        return;
      }
    }
    if (!isReplayCompatible(payload)) {
      this.error = tf('rp.verOld', { v: payload.version, cur: REPLAY_VERSION });
      return;
    }
    if (this.mgr.current !== this) return; // 等待 fetch 期间用户已离开（历史 bug：被强拉回回放）
    this.mgr.shared.audio.playSfx('select');
    this.mgr.switchTo('game', { replay: payload });
  },

  update() {
    const audio = this.mgr.shared.audio;
    const items = this._items();
    if (Input.pressed('cancel')) {
      audio.playSfx('select');
      this.mgr.switchTo('title');
      return;
    }
    if (valueDir(Input, audio) !== 0) {
      this.tab = this.tab === 'local' ? 'online' : 'local';
      this.idx = 0;
      this.error = null;
      return;
    }
    const nav = menuNav(Input, audio, this.idx, Math.max(1, items.length));
    if (nav !== null) this.idx = nav;
    if (Input.pressed('confirm') && items.length > 0) {
      this._play();
    }
  },

  draw(r) {
    r.text(t('rp.title'), VIEW_W / 2, 24, { size: 22, color: '#fff', align: 'center' });
    r.text(t('rp.sub'), VIEW_W / 2, 52, { size: 11, color: '#8cf', align: 'center' });
    // 选项卡
    const tabs = [['local', t('rp.tabLocal')], ['online', t('rp.tabOnline')]];
    for (let i = 0; i < tabs.length; i++) {
      const sel = this.tab === tabs[i][0];
      r.text(tabs[i][1], VIEW_W / 2 - 90 + i * 180, 80, { size: 14, color: sel ? '#ff4' : '#778', align: 'center' });
      if (sel) r.rect(VIEW_W / 2 - 90 + i * 180 - 56, 98, 112, 2, { fill: '#ff4' });
    }
    r.text(t('rp.hint'), VIEW_W / 2, 112, { size: 10, color: '#889', align: 'center' });

    const items = this._items();
    if (this.error) {
      r.text(this.error, VIEW_W / 2, 160, { size: 12, color: '#f88', align: 'center' });
    }
    if (items.length === 0) {
      const msg = this.tab === 'local'
        ? t('rp.emptyLocal')
        : this.online === null ? t('rp.loading') : t('rp.emptyOnline');
      r.text(msg, VIEW_W / 2, 180, { size: 13, color: '#8cf', align: 'center' });
      return;
    }

    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      const y = 150 + i * 42;
      const sel = i === this.idx;
      if (sel) r.rect(24, y - 6, VIEW_W - 48, 38, { fill: '#112', stroke: '#ff4' });
      const color = sel ? '#ff4' : '#ccd';
      if (this.tab === 'local') {
        const outdated = !isReplayCompatible(it);
        const modeTag = it.mode === 'practice' ? t('rp.prac') + ' · ' : '';
        r.text(`${modeTag}${diffLabel(it.difficulty)} · ${charLabel(it.character)}`, 40, y, { size: 13, color: outdated ? '#667' : color });
        r.text(`${t('rp.score')}${it.score}`, VIEW_W - 40, y, { size: 12, color: outdated ? '#667' : '#8cf', align: 'right' });
        const info = outdated ? t('rp.outdated') : `${it.cleared ? t('rp.cleared') : `STAGE ${it.stageReached}`} · ${String(it.date || '').slice(0, 16).replace('T', ' ')}`;
        r.text(info, 40, y + 18, { size: 10, color: outdated ? '#a55' : '#889' });
      } else {
        // 在线成绩：元数据带录像版本则按版本标可播性（过旧灰显）
        const outdated = it.replayVersion !== undefined && it.replayVersion !== REPLAY_VERSION;
        const rowColor = outdated ? '#667' : color;
        r.text(`${String(it.name).slice(0, 10)} · ${diffLabel(it.difficulty)} · ${charLabel(it.character)}`, 40, y, { size: 13, color: rowColor });
        r.text(String(it.score), VIEW_W - 40, y, { size: 12, color: outdated ? '#667' : '#8cf', align: 'right' });
        const info = outdated
          ? tf('rp.onlineOld', { v: it.replayVersion, cur: REPLAY_VERSION })
          : `${it.cleared ? t('rp.cleared') : `STAGE ${it.stageReached}`} · ${String(it.date || '').slice(0, 10)}`;
        r.text(info, 40, y + 18, { size: 10, color: outdated ? '#a55' : '#889' });
      }
    }
  },
};
